import React, { createContext, useContext, useEffect, useState } from 'react'
import type { GenerationCard } from '../types/generationCard'
import type { StyleCategory } from '../data/styles'
import { fetchStyles } from '../api/styles'
import { activeDiscount } from '../utils/discount'
import { useAuth } from './AuthContext'

interface StylesContextValue {
	styles: GenerationCard[]
	categories: StyleCategory[]
	popularStyles: GenerationCard[]
	loading: boolean
	error: string | null
}

const StylesContext = createContext<StylesContextValue>({
	styles: [],
	categories: [],
	popularStyles: [],
	loading: true,
	error: null,
})

export function StylesProvider({ children }: { children: React.ReactNode }) {
	const [styles, setStyles] = useState<GenerationCard[]>([])
	const [categories, setCategories] = useState<StyleCategory[]>([])
	const [popularIds, setPopularIds] = useState<Set<string>>(new Set())
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	// Рефетч стилей при смене user (логин/логаут): бэк отдаёт гостю урезанные
	// промпты (promptLocked), зареганому — полные.
	const { user } = useAuth()

	useEffect(() => {
		let cancelled = false

		fetchStyles()
			.then((data) => {
				if (cancelled) return

				const popIds = new Set<string>()
				const cards: GenerationCard[] = data.styles.map((s) => {
					if (s.isPopular) popIds.add(s.id)
					// Акция: на время скидки эффективная цена (= что спишет бэк)
					// подменяет priceOverride, % и остаток срока идут в плашку.
					const discount = activeDiscount(s.priceOverride, s.discountPrice, s.discountUntil)
					const defaultMediaUrl =
						s.categories?.find((c) => c.defaultMediaUrl)?.defaultMediaUrl ?? undefined
					// Полная карта slug → reference-медиа: галерея с известной
					// категорией подставит файл именно своей категории.
					const defaultMediaByCategory: Record<string, string> = {}
					for (const c of s.categories ?? []) {
						if (c.defaultMediaUrl) defaultMediaByCategory[c.slug] = c.defaultMediaUrl
					}
					return {
						id: s.id,
						type: 'style' as const,
						previewUrl: s.previewUrl,
						previewUrlVariants: s.previewUrlVariants,
						prompt: s.prompt,
						promptLocked: s.promptLocked,
						modelId: s.modelId,
						modelName: s.modelName,
						isVideo: s.isVideo,
						settings: s.settings,
						title: s.title,
						categorySlug: s.categorySlug,
						categorySlugs: s.categorySlugs,
						defaultMediaUrl: defaultMediaUrl ?? undefined,
						defaultMediaByCategory: Object.keys(defaultMediaByCategory).length
							? defaultMediaByCategory
							: undefined,
						usageCount: s.usageCount,
						sortOrder: s.sortOrder,
						popularSort: s.popularSort ?? null,
						demoBeforeUrl: s.demoBeforeUrl,
						demoVariants: s.demoVariants,
						// Поля «инструмента» (тренды = карточки Популярных)
						priceOverride: discount ? discount.price : s.priceOverride,
						discountPercent: discount?.percent,
						discountLeftLabel: discount?.leftLabel,
						// Срок акции едет в пин: пока промо живо, виджет показывает его
						// КАК ЕСТЬ (бэк спишет столько же), не режет floor'ом max().
						discountUntil: discount?.until,
						regularPrice: discount ? s.priceOverride : undefined,
						referenceVideoUrl: s.referenceVideoUrl,
						userVideoLocked: s.userVideoLocked,
						referenceAudioUrl: s.referenceAudioUrl,
						userAudioLocked: s.userAudioLocked,
						hasRefVideo: s.hasRefVideo,
						hasRefAudio: s.hasRefAudio,
					}
				})

				setStyles(cards)
				setCategories(data.categories)
				setPopularIds(popIds)
				setLoading(false)
			})
			.catch((err) => {
				if (cancelled) return
				setError(err?.message || 'Ошибка загрузки стилей')
				setStyles([])
				setCategories([])
				setLoading(false)
			})

		return () => {
			cancelled = true
		}
	}, [user])

	// «Популярное» определяется ТОЛЬКО админским флагом is_popular.
	// Снятие галочки в админке сразу убирает стиль из блока.
	// Порядок: СНАЧАЛА админский popularSort (DnD в табе «Популярные»
	// конструктора промптов), стили без индекса — в хвосте по usageCount.
	const popularStyles = styles
		.filter((s) => popularIds.has(s.id))
		.sort(
			(a, b) =>
				(a.popularSort ?? Number.MAX_SAFE_INTEGER) - (b.popularSort ?? Number.MAX_SAFE_INTEGER) ||
				(b.usageCount ?? 0) - (a.usageCount ?? 0),
		)

	return (
		<StylesContext.Provider value={{ styles, categories, popularStyles, loading, error }}>
			{children}
		</StylesContext.Provider>
	)
}

// eslint-disable-next-line react-refresh/only-export-components
export function useStylesContext() {
	return useContext(StylesContext)
}
